import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import SportsRoundedIcon from '@mui/icons-material/SportsRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import LoadingState from '../../components/ui/LoadingState';
import EmptyState from '../../components/ui/EmptyState';
import api from '../../api';
import tokens from '../../theme/tokens';
import { dataPelada } from '../../utils/dataPelada';

const FILTRO_KEY = 'rfc_peladas_filtro';

export { dataPelada }; // usado também pela tela de estatísticas da pelada

const Numero = ({ icone, valor, label }) => (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: 'text.secondary' }} title={label}>
    {icone}
    <Typography variant="body2" fontWeight={600} color="text.primary">{valor}</Typography>
    <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'inline' } }}>{label}</Typography>
  </Stack>
);

// Menu Peladas (Admin/Diretor): todas as peladas, com filtro por status; toque para lançar as estatísticas.
const PeladasPage = () => {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState(() => {
    try { return localStorage.getItem(FILTRO_KEY) || 'todas'; } catch (e) { return 'todas'; }
  });
  const [peladas, setPeladas] = useState(null);

  useEffect(() => {
    setPeladas(null);
    const status = filtro === 'abertas' ? 'Open' : filtro === 'fechadas' ? 'Closed' : undefined;
    api
      .get('peladas', { params: status ? { status } : {} })
      .then((r) => setPeladas(r.data.peladas || []))
      .catch(() => setPeladas([]));
    try { localStorage.setItem(FILTRO_KEY, filtro); } catch (e) { /* ignore */ }
  }, [filtro]);

  return (
    <AppLayout maxWidth="md">
      <PageHeader eyebrow="Diretoria" title="Peladas" subtitle="Todas as peladas. Toque numa pelada para lançar gols, assistências e cartões por jogador." />

      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        color="primary"
        value={filtro}
        onChange={(e, v) => v && setFiltro(v)}
        sx={{ mb: 2, bgcolor: 'background.paper' }}
      >
        <ToggleButton value="todas">Todas</ToggleButton>
        <ToggleButton value="abertas">Abertas</ToggleButton>
        <ToggleButton value="fechadas">Fechadas</ToggleButton>
      </ToggleButtonGroup>

      {peladas === null ? (
        <LoadingState label="Carregando peladas..." />
      ) : peladas.length === 0 ? (
        <Card><EmptyState title="Nenhuma pelada encontrada" description="Tente outro filtro." /></Card>
      ) : (
        <Stack spacing={1.25}>
          {peladas.map((p) => {
            const aberta = p.status === 'Open';
            return (
              <Card key={p._id} sx={{ borderLeft: `4px solid ${aberta ? tokens.color.success : tokens.color.border}` }}>
                <CardActionArea onClick={() => navigate(`/peladas/${p._id}`)} sx={{ p: 1.75 }}>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                        <Typography fontWeight={700} sx={{ textTransform: 'capitalize' }} noWrap>{dataPelada(p.date)}</Typography>
                        {p.time && <Typography variant="body2" color="text.secondary">{p.time}</Typography>}
                      </Stack>
                      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Chip size="small" label={aberta ? 'Aberta' : 'Fechada'} color={aberta ? 'success' : 'default'} variant={aberta ? 'filled' : 'outlined'} />
                        <Numero icone={<GroupsRoundedIcon fontSize="small" />} valor={p.jogadores} label="jogadores" />
                        <Numero icone={<SportsRoundedIcon fontSize="small" />} valor={p.partidas} label="partidas" />
                        <Numero icone={<SportsSoccerRoundedIcon fontSize="small" />} valor={p.gols} label="gols" />
                        {p.estatisticasLancadas && (
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ color: tokens.color.success }}>
                            <CheckCircleRoundedIcon sx={{ fontSize: 16 }} />
                            <Typography variant="caption" fontWeight={600}>Estatísticas lançadas</Typography>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                    <ChevronRightRoundedIcon color="action" />
                  </Stack>
                </CardActionArea>
              </Card>
            );
          })}
        </Stack>
      )}
    </AppLayout>
  );
};

export default PeladasPage;
